import { useCallback, useEffect, useState } from 'react';
import { FileText, Check, X } from 'lucide-react';
import { apiClient } from '../../lib/api-client';
import { Badge } from '../ui/Badge';
import { Button } from '../ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/Card';
import { Skeleton } from '../ui/Skeleton';

export interface EvolutionProposal {
  id: string;
  title: string;
  rationale?: string;
  status?: string;
}

function normalizeProposal(raw: Record<string, unknown>, index: number): EvolutionProposal {
  return {
    id: String(raw.id ?? raw.proposalId ?? `proposal-${index}`),
    title: String(raw.title ?? raw.name ?? 'Proposal'),
    rationale:
      raw.rationale != null
        ? String(raw.rationale)
        : raw.description != null
          ? String(raw.description)
          : raw.summary != null
            ? String(raw.summary)
            : undefined,
    status: raw.status != null ? String(raw.status) : undefined,
  };
}

export interface ProposalsTabProps {
  streamId: string;
  canManage: boolean;
}

export function ProposalsTab({ streamId, canManage }: ProposalsTabProps) {
  const [proposals, setProposals] = useState<EvolutionProposal[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!streamId) return;
    setIsLoading(true);
    setError(null);
    try {
      const res = await apiClient.getAiWorkflowEvolutionProposals(streamId);
      const list = Array.isArray(res.proposals)
        ? res.proposals.map((p, i) => normalizeProposal(p as Record<string, unknown>, i))
        : [];
      setProposals(list);
      setError(null);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to load proposals');
      setProposals([]);
    } finally {
      setIsLoading(false);
    }
  }, [streamId]);

  useEffect(() => {
    void load();
  }, [load]);

  const handleDecision = async (proposalId: string, status: 'approved' | 'rejected') => {
    setBusyId(proposalId);
    try {
      await apiClient.updateAiWorkflowEvolutionProposal(streamId, proposalId, { status });
      setError(null);
      await load();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Update failed');
    } finally {
      setBusyId(null);
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        {[1, 2, 3].map((i) => (
          <Skeleton key={i} className="h-32 w-full rounded-lg" />
        ))}
      </div>
    );
  }

  if (error && proposals.length === 0) {
    return (
      <Card className="border-red-200 dark:border-red-900/40">
        <CardContent className="py-6 text-sm text-red-700 dark:text-red-300">{error}</CardContent>
      </Card>
    );
  }

  if (proposals.length === 0) {
    return (
      <Card>
        <CardContent className="py-12 text-center text-gray-500 dark:text-gray-400">
          <FileText className="w-10 h-10 mx-auto mb-3 opacity-60" />
          <p>No pending proposals for this stream.</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {error && (
        <div className="text-sm text-amber-800 dark:text-amber-200 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-900 rounded-md px-3 py-2">
          {error}
        </div>
      )}
      {proposals.map((p) => {
        const st = (p.status || '').toLowerCase();
        const decided = st === 'approved' || st === 'rejected';
        return (
          <Card key={p.id} className="border-gray-200 dark:border-gray-700">
            <CardHeader className="pb-2">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="space-y-1 min-w-0">
                  <CardTitle className="text-base text-gray-900 dark:text-gray-100">{p.title}</CardTitle>
                  {p.status && (
                    <Badge variant="outline" className="capitalize">
                      {p.status}
                    </Badge>
                  )}
                </div>
                {canManage && !decided && (
                  <div className="flex items-center gap-2 shrink-0">
                    <Button
                      size="sm"
                      variant="outline"
                      className="text-green-700 border-green-300 hover:bg-green-50 dark:text-green-300 dark:border-green-700 dark:hover:bg-green-950/40"
                      disabled={busyId === p.id}
                      onClick={() => handleDecision(p.id, 'approved')}
                    >
                      <Check className="w-4 h-4 mr-1" />
                      Approve
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="text-red-700 border-red-300 hover:bg-red-50 dark:text-red-300 dark:border-red-800 dark:hover:bg-red-950/40"
                      disabled={busyId === p.id}
                      onClick={() => handleDecision(p.id, 'rejected')}
                    >
                      <X className="w-4 h-4 mr-1" />
                      Reject
                    </Button>
                  </div>
                )}
              </div>
            </CardHeader>
            {p.rationale && (
              <CardContent className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed whitespace-pre-wrap">
                {p.rationale}
              </CardContent>
            )}
          </Card>
        );
      })}
    </div>
  );
}
